import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getClientOrders, getClientAppointments, getRewards, getServices, createAppointment, getAvailableBlocks } from '../lib/api';
import { formatCurrency, getStatusLabel, getVehicleTypeLabel, formatTime, formatDate } from '../lib/utils';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { 
  Car, 
  Calendar as CalendarIcon, 
  Star, 
  Gift,
  LogOut,
  Clock,
  CheckCircle,
  Loader2,
  Plus,
  History,
  Sparkles
} from 'lucide-react';
import { toast } from 'sonner';
import { format, addDays } from 'date-fns';
import { es } from 'date-fns/locale';
import { cn } from '../lib/utils';
import { useNavigate } from 'react-router-dom';

const VEHICLE_TYPES = [
  { value: 'sedan', label: 'Sedán' },
  { value: 'suv', label: 'SUV' },
  { value: 'pickup', label: 'Pickup' },
  { value: 'motocicleta', label: 'Motocicleta' },
  { value: 'otro', label: 'Otro' },
];

export default function ClientPortalPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [rewards, setRewards] = useState([]);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // New appointment modal
  const [showNewAppointment, setShowNewAppointment] = useState(false);
  const [appointmentData, setAppointmentData] = useState({
    service_id: '',
    vehicle_type: 'sedan',
    date: null,
    time_block: '',
    notes: ''
  });
  const [availableSlots, setAvailableSlots] = useState([]);
  const [loadingSlots, setLoadingSlots] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchData();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchData = async () => {
    setLoading(true);
    try {
      const [ordersRes, appointmentsRes, rewardsRes, servicesRes] = await Promise.all([
        getClientOrders(),
        getClientAppointments(),
        getRewards(),
        getServices()
      ]);
      setOrders(ordersRes.data);
      setAppointments(appointmentsRes.data);
      setRewards(rewardsRes.data);
      setServices(servicesRes.data);
    } catch (error) {

    }
    setLoading(false);
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const fetchAvailableSlots = async (date) => {
    setLoadingSlots(true);
    try {
      const dateStr = format(date, 'yyyy-MM-dd');
      const res = await getAvailableBlocks(dateStr);
      // Convert blocks to flat list for backwards compatibility
      const blocks = res.data;
      const slots = [];
      if (blocks.manana?.available) slots.push('manana');
      if (blocks.mediodia?.available) slots.push('mediodia');
      if (blocks.tarde?.available) slots.push('tarde');
      setAvailableSlots(slots);
    } catch (error) {
      setAvailableSlots([]);
    }
    setLoadingSlots(false);
  };

  const handleDateSelect = (date) => {
    setAppointmentData({ ...appointmentData, date, time_block: '' });
    fetchAvailableSlots(date);
  };

  const handleCreateAppointment = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    
    try {
      await createAppointment({
        client_id: user.id,
        name: user.name,
        phone: user.phone,
        vehicle_type: appointmentData.vehicle_type,
        service_id: appointmentData.service_id,
        date: format(appointmentData.date, 'yyyy-MM-dd'),
        time_block: appointmentData.time_block,
        notes: appointmentData.notes || null
      });
      
      toast.success('Solicitud enviada. Será confirmada por el administrador.');
      setShowNewAppointment(false);
      setAppointmentData({
        service_id: '',
        vehicle_type: 'sedan',
        date: null,
        time_block: '',
        notes: ''
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al agendar');
    }
    setSubmitting(false);
  };

  // Active order (most recent non-delivered)
  const activeOrder = orders.find(o => o.service_status !== 'entregado');

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-slate-900 text-white">
        <div className="max-w-4xl mx-auto px-4 py-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="https://customer-assets.emergentagent.com/job_carwash-quick-pos/artifacts/tady4vql_image.png" alt="WD Logo" className="w-10 h-10 object-contain" />
              <span className="text-xl font-bold">WD Lavado</span>
            </div>
            <button 
              onClick={handleLogout}
              className="flex items-center gap-2 text-slate-300 hover:text-white transition-colors"
              data-testid="client-logout-btn"
            >
              <LogOut className="w-5 h-5" />
              Salir
            </button>
          </div>
          
          <div className="mt-6">
            <div className="text-slate-400 text-sm">Bienvenido</div>
            <div className="text-2xl font-bold">{user?.name}</div>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-6 space-y-6">
        {/* Loyalty Card with Code */}
        <div className="loyalty-card animate-fadeIn" data-testid="loyalty-card">
          <div className="relative z-10">
            <div className="flex items-start justify-between">
              <div>
                <div className="text-white/70 text-sm mb-1">Puntos acumulados</div>
                <div className="flex items-center gap-3">
                  <Star className="w-10 h-10 text-amber-400" />
                  <span className="text-5xl font-bold">{user?.points || 0}</span>
                </div>
                <div className="mt-4 text-white/70 text-sm">
                  1 punto = $1 MXN de descuento
                </div>
              </div>
              {/* Client Code */}
              <div className="text-right">
                <div className="text-white/70 text-xs mb-1">Tu código</div>
                <div className="bg-white/20 backdrop-blur px-4 py-2 rounded-lg">
                  <span className="text-3xl font-bold font-mono tracking-wider" data-testid="client-code">
                    {user?.code || '----'}
                  </span>
                </div>
                <div className="text-white/60 text-xs mt-1">Muéstralo al pagar</div>
              </div>
            </div>
          </div>
        </div>

        {/* Referral Section */}
        <div className="card-base p-6 animate-fadeIn" data-testid="referral-section">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
              <Gift className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <h3 className="font-semibold text-slate-900">Invita a un amigo</h3>
              <p className="text-sm text-slate-500">Comparte tu código y ambos ganan puntos</p>
            </div>
          </div>
          <div className="bg-slate-50 rounded-xl p-4 mb-3">
            <div className="text-xs text-slate-500 mb-1">Tu link de referido</div>
            <div className="flex items-center gap-2">
              <input
                readOnly
                value={`${window.location.origin}/lealtad?ref=${user?.code || ''}`}
                className="flex-1 bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono text-slate-700"
                data-testid="referral-link-input"
              />
              <Button
                size="sm"
                variant="outline"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/lealtad?ref=${user?.code || ''}`);
                  toast.success('Link copiado');
                }}
                data-testid="copy-referral-btn"
              >
                Copiar
              </Button>
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3 text-center">
            <div className="p-3 bg-emerald-50 rounded-lg">
              <div className="text-lg font-bold text-emerald-700">+20 pts</div>
              <div className="text-xs text-slate-500">Para ti al referir</div>
            </div>
            <div className="p-3 bg-blue-50 rounded-lg">
              <div className="text-lg font-bold text-blue-700">+50 pts</div>
              <div className="text-xs text-slate-500">Para tu amigo</div>
            </div>
          </div>
        </div>

        {/* Active Order Status */}
        {activeOrder && (
          <div className="card-base p-6 border-l-4 border-l-blue-600 animate-fadeIn" data-testid="active-order">
            <div className="flex items-center gap-2 text-blue-600 font-semibold mb-3">
              <Clock className="w-5 h-5" />
              Servicio en Proceso
            </div>
            <div className="flex items-center justify-between">
              <div>
                <div className="font-mono text-slate-500">{activeOrder.folio}</div>
                <div className="font-semibold text-slate-900 mt-1">
                  {activeOrder.services?.join(', ')}
                </div>
                <div className="text-sm text-slate-600 mt-1">
                  {activeOrder.vehicle && getVehicleTypeLabel(activeOrder.vehicle.type)}
                  {activeOrder.vehicle?.plates && ` - ${activeOrder.vehicle.plates}`}
                </div>
              </div>
              <div className="text-right">
                <span className={`status-badge status-${activeOrder.service_status}`}>
                  {getStatusLabel(activeOrder.service_status)}
                </span>
              </div>
            </div>
          </div>
        )}

        {/* Quick Actions */}
        <div className="grid grid-cols-2 gap-4">
          <Button
            className="h-16 text-lg gap-2"
            onClick={() => setShowNewAppointment(true)}
            data-testid="new-appointment-btn"
          >
            <Plus className="w-5 h-5" />
            Nueva Cita
          </Button>
          <Button
            variant="outline"
            className="h-16 text-lg gap-2"
            onClick={fetchData}
          >
            <History className="w-5 h-5" />
            Actualizar
          </Button>
        </div>

        {/* Tabs */}
        <Tabs defaultValue="appointments">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="appointments" data-testid="tab-appointments">
              <CalendarIcon className="w-4 h-4 mr-2" />
              Citas
            </TabsTrigger>
            <TabsTrigger value="history" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              Historial
            </TabsTrigger>
            <TabsTrigger value="rewards" data-testid="tab-rewards">
              <Gift className="w-4 h-4 mr-2" />
              Recompensas
            </TabsTrigger>
          </TabsList>

          {/* Appointments Tab */}
          <TabsContent value="appointments" className="mt-6">
            {appointments.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tienes citas programadas</p>
              </div>
            ) : (
              <div className="space-y-4">
                {appointments.map((apt) => (
                  <div 
                    key={apt.id}
                    className={`card-base p-4 ${
                      apt.status === 'agendado' ? 'border-l-4 border-l-purple-500' :
                      apt.status === 'en_proceso' ? 'border-l-4 border-l-blue-500' :
                      apt.status === 'listo' ? 'border-l-4 border-l-emerald-500' :
                      'border-l-4 border-l-slate-300'
                    }`}
                    data-testid={`appointment-card-${apt.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="font-semibold text-slate-900">{apt.service}</div>
                        <div className="text-sm text-slate-600 mt-1">
                          {getVehicleTypeLabel(apt.vehicle_type)}
                        </div>
                        <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                          <span className="flex items-center gap-1">
                            <CalendarIcon className="w-4 h-4" />
                            {apt.date}
                          </span>
                          <span className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            {formatTime(apt.time)}
                          </span>
                        </div>
                      </div>
                      <span className={`status-badge status-${apt.status}`}>
                        {getStatusLabel(apt.status)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* History Tab */}
          <TabsContent value="history" className="mt-6">
            {orders.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <History className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No tienes historial de servicios</p>
              </div>
            ) : (
              <div className="space-y-4">
                {orders.map((order) => (
                  <div key={order.id} className="card-base p-4" data-testid={`order-history-${order.id}`}>
                    <div className="flex items-start justify-between mb-3">
                      <div>
                        <div className="font-mono text-blue-600">{order.folio}</div>
                        <div className="text-sm text-slate-500">{order.date}</div>
                      </div>
                      <span className={`status-badge status-${order.service_status}`}>
                        {getStatusLabel(order.service_status)}
                      </span>
                    </div>
                    
                    <div className="flex flex-wrap gap-2 mb-3">
                      {order.services?.map((s, i) => (
                        <span 
                          key={i}
                          className="px-2 py-1 bg-slate-100 rounded text-xs font-medium"
                        >
                          {s}
                        </span>
                      ))}
                    </div>
                    
                    <div className="flex items-center justify-between pt-3 border-t border-slate-100">
                      <div className="text-slate-600">
                        {order.vehicle && (
                          <>
                            {getVehicleTypeLabel(order.vehicle.type)}
                            {order.vehicle.plates && ` • ${order.vehicle.plates}`}
                          </>
                        )}
                      </div>
                      <div className="text-right">
                        <div className="font-bold text-slate-900">{formatCurrency(order.total)}</div>
                        {order.points_earned > 0 && (
                          <div className="text-xs text-amber-600 flex items-center gap-1 justify-end">
                            <Star className="w-3 h-3" />
                            +{order.points_earned} puntos
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Rewards Tab */}
          <TabsContent value="rewards" className="mt-6">
            {rewards.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <Gift className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No hay recompensas disponibles</p>
              </div>
            ) : (
              <div className="grid gap-4">
                {rewards.map((reward) => {
                  const canRedeem = (user?.points || 0) >= reward.points_required;
                  return (
                    <div 
                      key={reward.id}
                      className={`card-base p-4 ${canRedeem ? 'border-2 border-emerald-200 bg-emerald-50/50' : ''}`}
                      data-testid={`reward-${reward.id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <div className="font-semibold text-slate-900">{reward.name}</div>
                          {reward.description && (
                            <div className="text-sm text-slate-600 mt-1">{reward.description}</div>
                          )}
                        </div>
                        <div className="text-right">
                          <div className="flex items-center gap-1 text-amber-600 font-bold">
                            <Star className="w-5 h-5" />
                            {reward.points_required}
                          </div>
                          {canRedeem && (
                            <div className="text-xs text-emerald-600 mt-1 flex items-center gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Disponible
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                <div className="text-center text-sm text-slate-500 mt-4">
                  Para canjear recompensas, menciona al cajero tu recompensa al momento del pago.
                </div>
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* New Appointment Modal */}
      <Dialog open={showNewAppointment} onOpenChange={setShowNewAppointment}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Nueva Cita</DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleCreateAppointment} className="space-y-4">
            <div>
              <Label>Servicio *</Label>
              <Select
                value={appointmentData.service_id}
                onValueChange={(value) => setAppointmentData({ ...appointmentData, service_id: value })}
              >
                <SelectTrigger className="mt-1" data-testid="new-apt-service">
                  <SelectValue placeholder="Selecciona un servicio" />
                </SelectTrigger>
                <SelectContent>
                  {services.map((service) => (
                    <SelectItem key={service.id} value={service.id}>
                      {service.name} - {formatCurrency(service.price)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Tipo de vehículo *</Label>
              <Select
                value={appointmentData.vehicle_type}
                onValueChange={(value) => setAppointmentData({ ...appointmentData, vehicle_type: value })}
              >
                <SelectTrigger className="mt-1" data-testid="new-apt-vehicle">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VEHICLE_TYPES.map((type) => (
                    <SelectItem key={type.value} value={type.value}>
                      {type.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Fecha *</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button
                    variant="outline"
                    className={cn(
                      "w-full justify-start text-left font-normal mt-1",
                      !appointmentData.date && "text-slate-500"
                    )}
                    data-testid="new-apt-date"
                  >
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {appointmentData.date ? (
                      format(appointmentData.date, "d 'de' MMMM, yyyy", { locale: es })
                    ) : (
                      "Selecciona una fecha"
                    )}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={appointmentData.date}
                    onSelect={handleDateSelect}
                    disabled={(date) => date < new Date() || date > addDays(new Date(), 30)}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
            
            {appointmentData.date && (
              <div className="animate-fadeIn">
                <Label>Hora *</Label>
                {loadingSlots ? (
                  <div className="flex items-center gap-2 mt-2 text-slate-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Cargando horarios...
                  </div>
                ) : availableSlots.length === 0 ? (
                  <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm">
                    No hay horarios disponibles para esta fecha.
                  </div>
                ) : (
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    {[
                      { key: 'manana', label: 'Mañana', time: '8:30 - 10:30' },
                      { key: 'mediodia', label: 'Medio Día', time: '10:30 - 1:00' },
                      { key: 'tarde', label: 'Tarde', time: '1:00 - 3:30' },
                    ].filter(b => availableSlots.includes(b.key)).map((block) => (
                      <button
                        key={block.key}
                        type="button"
                        className={cn(
                          "p-3 border-2 rounded-lg text-sm transition-all text-center",
                          appointmentData.time_block === block.key
                            ? "border-blue-600 bg-blue-50"
                            : "border-slate-200 hover:border-blue-400"
                        )}
                        onClick={() => setAppointmentData({ ...appointmentData, time_block: block.key })}
                        data-testid={`new-apt-block-${block.key}`}
                      >
                        <div className="font-medium">{block.label}</div>
                        <div className="text-xs text-slate-500">{block.time}</div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            <Button
              type="submit"
              className="w-full h-14"
              disabled={!appointmentData.service_id || !appointmentData.date || !appointmentData.time_block || submitting}
              data-testid="submit-new-apt-btn"
            >
              {submitting ? (
                <Loader2 className="w-5 h-5 animate-spin" />
              ) : (
                <>
                  <Sparkles className="w-5 h-5 mr-2" />
                  Agendar Cita
                </>
              )}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
